// Standalone Luxury Wishlist Handler (localStorage + Live Sync + App Compatible)
import { CartLinesUpdateEvent } from '@shopify/events';

const WISHLIST_KEY = 'semade_wishlist_items';

export class WishlistManager {
  constructor() {
    this.init();
  }

  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  setWishlist(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      this.updateUI();
    } catch (e) {
      console.error('Wishlist storage error', e);
    }
  }

  toggleItem(item) {
    let list = this.getWishlist();
    const index = list.findIndex(i => i.id === item.id || i.handle === item.handle);
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(item);
    }
    this.setWishlist(list);
  }

  removeItem(id) {
    let list = this.getWishlist();
    list = list.filter(i => i.id !== id && i.handle !== id);
    this.setWishlist(list);
  }

  updateUI() {
    const list = this.getWishlist();
    const count = list.length;
    
    // Update all counter badges
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.classList.add('has-items');
      } else {
        el.classList.remove('has-items');
      }
    });

    // Update heart icons on page
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const handle = btn.dataset.productHandle;
      const isSaved = list.some(i => i.handle === handle);
      if (isSaved) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    // Render Drawer Content if open
    this.renderDrawer(list);
  }

  renderDrawer(list) {
    const emptyState = document.getElementById('wishlist-empty-state');
    const grid = document.getElementById('wishlist-grid');
    if (!emptyState || !grid) return;

    if (list.length === 0) {
      emptyState.style.display = 'flex';
      grid.style.display = 'none';
      grid.innerHTML = '';
      return;
    }

    emptyState.style.display = 'none';
    grid.style.display = 'flex';
    grid.innerHTML = list.map(item => `
      <div class="wishlist-item" data-wishlist-id="${item.id || item.handle}">
        <img src="${item.image || ''}" alt="${item.title || ''}" class="wishlist-item__image" />
        <div class="wishlist-item__info">
          <a href="${item.url || '#'}" class="wishlist-item__title">${item.title || ''}</a>
          <span class="wishlist-item__price">${item.price || ''}</span>
        </div>
        <div class="wishlist-item__actions">
          <button type="button" class="wishlist-item__add-btn" data-variant-id="${item.variantId || ''}">Add to Bag</button>
          <button type="button" class="wishlist-item__remove-btn" data-remove-id="${item.id || item.handle}">Remove</button>
        </div>
      </div>
    `).join('');

    // Attach remove handlers
    grid.querySelectorAll('[data-remove-id]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.currentTarget.dataset.removeId;
        this.removeItem(id);
      });
    });

    // Attach Add to Bag handlers
    grid.querySelectorAll('[data-variant-id]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const variantId = e.currentTarget.dataset.variantId;
        if (!variantId) return;

        const originalLabel = btn.textContent;
        const cartItemSections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((component) => component.dataset.sectionId)
          .filter(Boolean);
        const cartUpdate = CartLinesUpdateEvent.createPromise();

        btn.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'wishlist',
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: cartUpdate.promise,
          })
        );

        try {
          btn.textContent = 'Adding...';
          const response = await fetch(Theme.routes.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              sections: cartItemSections.join(','),
              sections_url: window.location.pathname,
            }),
          });

          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add this item to your bag.');
          }

          const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
            headers: { Accept: 'application/json' },
          });
          if (!cartResponse.ok) throw new Error('Your bag could not be refreshed.');

          const cart = await cartResponse.json();
          cartUpdate.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError: false,
              itemCount: 1,
              sections: result.sections,
              source: 'wishlist',
            },
          });

          btn.textContent = 'Added!';

          // Let the cart drawer open after this modal restores focus.
          btn.closest('dialog')?.close();
        } catch (err) {
          cartUpdate.reject(err);
          btn.textContent = 'Error';
          console.error('[wishlist] Add to bag failed:', err);
          setTimeout(() => {
            btn.textContent = originalLabel;
          }, 2000);
        }
      });
    });
  }

  init() {
    this.updateUI();

    // Trigger open drawer on wishlist click
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-wishlist-trigger]');
      if (trigger) {
        e.preventDefault();
        const dialog = document.getElementById('wishlist-drawer');
        if (dialog) {
          const d = dialog.querySelector('dialog');
          if (d && !d.open) {
            d.showModal();
            this.updateUI();
          }
        }
      }
    });

    // Toggle wishlist on product cards
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        const item = {
          id: btn.dataset.productId,
          handle: btn.dataset.productHandle,
          title: btn.dataset.productTitle,
          price: btn.dataset.productPrice,
          image: btn.dataset.productImage,
          url: btn.dataset.productUrl,
          variantId: btn.dataset.productVariantId
        };
        this.toggleItem(item);
      }
    });
  }
}

// Instantiate on load
if (typeof window !== 'undefined') {
  window.wishlistManager = new WishlistManager();
}
